import { GameObject, Rectangle, Vector2 } from '../../../types/game.types';
import { RenderManager } from '../../../core/RenderManager';
import { AudioManager } from '../../../core/AudioManager';

export class Paddle implements GameObject {
  public id: string;
  public active: boolean = true;
  public position: Vector2;
  public velocity: Vector2 = { x: 0, y: 0 };
  public size: Vector2;

  private renderManager: RenderManager;
  private audioManager: AudioManager;
  private movement: number = 0; // -1, 0, or 1
  
  // Paddle properties
  private paddleWidth: number;
  private paddleHeight: number;
  private readonly MOVE_SPEED = 400; // pixels per second
  private animationTimer: number = 0;

  constructor(
    id: string,
    x: number,
    y: number,
    width: number,
    height: number,
    renderManager: RenderManager,
    audioManager: AudioManager
  ) {
    this.id = id;
    this.position = { x, y };
    this.paddleWidth = width;
    this.paddleHeight = height;
    this.size = { x: width, y: height };
    this.renderManager = renderManager;
    this.audioManager = audioManager;
  }

  public update(deltaTime: number): void {
    // Update animation timer
    this.animationTimer += deltaTime;
    
    // Apply movement
    this.velocity.x = this.movement * this.MOVE_SPEED;
    
    // Update position
    this.position.x += this.velocity.x * deltaTime;
    
    // Keep paddle on screen
    const margin = 10;
    this.position.x = Math.max(margin, Math.min(800 - margin - this.paddleWidth, this.position.x));
  }

  public setMovement(direction: number): void {
    this.movement = Math.max(-1, Math.min(1, direction));
  }

  public getPosition(): Vector2 {
    return { ...this.position };
  }

  public getBounds(): Rectangle {
    return {
      x: this.position.x,
      y: this.position.y,
      width: this.paddleWidth,
      height: this.paddleHeight
    };
  }

  public render(ctx: CanvasRenderingContext2D): void {
    if (!this.active) return;
    
    const centerX = this.position.x + this.paddleWidth / 2;
    const centerY = this.position.y + this.paddleHeight / 2;
    
    // Main paddle body (rounded rectangle effect using multiple rectangles)
    const paddleColor = '#00AAFF'; // Blue paddle
    
    // Main body
    this.renderManager.fillRect(
      this.position.x + 4,
      this.position.y,
      this.paddleWidth - 8,
      this.paddleHeight,
      paddleColor
    );
    
    // Rounded ends
    this.renderManager.fillRect(
      this.position.x + 2,
      this.position.y + 1,
      4,
      this.paddleHeight - 2,
      paddleColor
    );
    this.renderManager.fillRect(
      this.position.x + this.paddleWidth - 6,
      this.position.y + 1,
      4,
      this.paddleHeight - 2,
      paddleColor
    );
    
    // Top highlight
    this.renderManager.fillRect(
      this.position.x + 4,
      this.position.y,
      this.paddleWidth - 8,
      2,
      '#FFFFFF'
    );
    
    // Side highlights
    this.renderManager.fillRect(
      this.position.x + 4,
      this.position.y,
      2,
      this.paddleHeight,
      '#CCFFFF'
    );
    this.renderManager.fillRect(
      this.position.x + this.paddleWidth - 6,
      this.position.y,
      2,
      this.paddleHeight,
      '#0088CC'
    );
    
    // Movement indicator (subtle glow when moving)
    if (Math.abs(this.movement) > 0) {
      const glowIntensity = 0.2 + Math.sin(this.animationTimer * 8) * 0.1;
      const glowColor = `rgba(0, 170, 255, ${glowIntensity})`;
      
      this.renderManager.strokeRect(
        this.position.x - 2,
        this.position.y - 2,
        this.paddleWidth + 4,
        this.paddleHeight + 4,
        glowColor,
        2
      );
    }
    
    // Center line for visual appeal
    this.renderManager.fillRect(
      centerX - 1,
      this.position.y + 2,
      2,
      this.paddleHeight - 4,
      '#004466'
    );
    
    // Grip texture lines
    for (let i = 0; i < 5; i++) {
      const lineX = this.position.x + 10 + i * (this.paddleWidth - 20) / 4;
      this.renderManager.fillRect(lineX, this.position.y + 3, 1, this.paddleHeight - 6, '#0088CC');
    }
  }
}